import type { MessageKey } from '$i18n/index';
import { TIME_BUCKETS } from '$domain/context-match';
import type { PlanContext } from '$domain/context-match';

/**
 * The onboarding step order, and the fixed suggestion sets.
 *
 * Suggestions are a starting point, never a menu the user has to fit into —
 * every list here ends in "something else" with a free-text field. Spec §13:
 * Comeback does not prescribe what the user's values should be.
 */

export const STEPS = [
  'intro',
  'directions',
  'expressions',
  'behaviors',
  'plans',
  'aloud',
  'placement',
  'arc',
  'done',
] as const;

export type Step = (typeof STEPS)[number];

export function nextStep(step: Step): Step {
  const index = STEPS.indexOf(step);
  return STEPS[Math.min(index + 1, STEPS.length - 1)] as Step;
}

export function previousStep(step: Step): Step {
  const index = STEPS.indexOf(step);
  return STEPS[Math.max(index - 1, 0)] as Step;
}

export function isStep(value: string): value is Step {
  return (STEPS as readonly string[]).includes(value);
}

/**
 * The suggested directions.
 *
 * Plain words, not abstractions: "The people I love" rather than
 * "relationships", because the second is a category and the first is a thought
 * someone actually has. The internal ids stay abstract — they are what the log
 * records, and they never reach the screen.
 */
export const SUGGESTED_DIRECTIONS = [
  { directionId: 'people', labelKey: 'onboard.direction.people' },
  { directionId: 'body', labelKey: 'onboard.direction.body' },
  { directionId: 'learning', labelKey: 'onboard.direction.learning' },
  { directionId: 'rest', labelKey: 'onboard.direction.rest' },
  { directionId: 'presence', labelKey: 'onboard.direction.presence' },
  { directionId: 'making', labelKey: 'onboard.direction.making' },
  { directionId: 'craft', labelKey: 'onboard.direction.craft' },
  { directionId: 'outside', labelKey: 'onboard.direction.outside' },
] as const satisfies readonly { directionId: string; labelKey: MessageKey }[];

/**
 * Suggested target behaviours.
 *
 * Behaviour-neutral by rule (architecture A9): none of these assumes a phone,
 * and the schema they land in is `targetBehaviors`, not `screenHabits`. The
 * domain is phones in v1; the vocabulary does not foreclose anything else.
 */
export const SUGGESTED_BEHAVIORS = [
  { behaviorId: 'scrolling', labelKey: 'onboard.behavior.scrolling' },
  { behaviorId: 'checking', labelKey: 'onboard.behavior.checking' },
  { behaviorId: 'putting_off', labelKey: 'onboard.behavior.putting_off' },
  { behaviorId: 'opening', labelKey: 'onboard.behavior.opening' },
] as const satisfies readonly { behaviorId: string; labelKey: MessageKey }[];

export const PLAN_CONTEXTS = [...TIME_BUCKETS, 'any'] as const satisfies readonly PlanContext[];

export function contextLabelKey(context: PlanContext): MessageKey {
  return `context.${context}` as MessageKey;
}
